import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function SuccessStoryDetail() {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/success-stories/${slug}`)
      .then((res) => {
        setStory(res.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center bg-[#F8FAFC] px-6">
        <p className="text-slate-500 mb-6">This success story could not be found.</p>
        <Link to="/success-stories">
          <Button variant="outline">← Back to Success Stories</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="success-story-detail">
      <Seo
        title={`${story.title} | Udvega Success Stories`}
        description={story.meta_description || story.one_line_achievement || story.title}
        image={story.summary_image || undefined}
      />
      {/* Hero header */}
      <section className="relative bg-slate-900 py-16 md:py-20 overflow-hidden">
        {story.summary_image && (
          <>
            <img src={story.summary_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
          </>
        )}
        <div className="max-w-4xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Link
              to="/success-stories"
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-300 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Success Stories
            </Link>

            <span className="inline-block px-3 py-1 rounded-full bg-teal-600/20 border border-teal-500/30 text-xs font-semibold text-teal-300 mb-4">
              {story.industry}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-4">
              {story.title}
            </h1>
            <p className="text-teal-300 font-medium mb-8">{story.one_line_achievement}</p>

            {/* Metrics highlight box */}
            <div className="grid grid-cols-3 gap-4 p-6 rounded-2xl bg-white/10 backdrop-blur border border-white/10">
              {story.stats.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="font-heading text-2xl md:text-3xl font-semibold text-white">{s.value}</p>
                  <p className="text-xs text-slate-400 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article body */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <motion.article
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="story-content bg-white rounded-3xl shadow-xl shadow-slate-300/25 p-8 md:p-12"
            dangerouslySetInnerHTML={{ __html: story.full_content }}
          />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-3xl bg-gradient-to-br from-teal-600 via-teal-700 to-slate-900 p-10 md:p-14 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 70% 50%, #2DD4BF 0%, transparent 50%)' }} />
            <div className="relative z-10">
              <Trophy className="w-10 h-10 text-teal-200 mx-auto mb-4" />
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3">
                Want to replicate these results in your team?
              </h2>
              <p className="text-teal-100 mb-8 max-w-lg mx-auto">
                Deploy Udvega&apos;s predictive culture intelligence and start reducing employee turnover in less than 10 minutes.
              </p>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-teal-700 hover:bg-teal-50 px-10 h-12 font-semibold shadow-xl"
                  data-testid="story-detail-cta-btn"
                >
                  Book a Demo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
